import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { fetchCouriers, createCourier, updateCourier, deleteCourier } from "./courierAsyncThunk";
import { Courier, DeleteCourier } from "../../../types/CourierTypes";
import { RequestResults } from "../../common/RequestStatus";

interface CourierState {
  data: Courier[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: CourierState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const courierSlice = createSlice({
  name: "courier",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch Couriers
    builder
      .addCase(fetchCouriers.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchCouriers.fulfilled, (state, action: PayloadAction<Courier[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchCouriers.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch couriers");
        state.error = action.payload || "Failed to fetch couriers";
      });

    // Add Courier
    builder
      .addCase(createCourier.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCourier.fulfilled, (state, action: PayloadAction<Courier>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createCourier.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add Courier");
        state.error = action.payload || "Failed to add Courier";
      });

    // Update Courier
    builder
      .addCase(updateCourier.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCourier.fulfilled, (state, action: PayloadAction<Courier>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateCourier.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update Courier");
        state.error = action.payload || "Failed to update Courier";
      });

    // Delete Courier
    builder
      .addCase(deleteCourier.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCourier.fulfilled, (state, action: PayloadAction<DeleteCourier>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data = state.data.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteCourier.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete Courier");
        state.error = action.payload || "Failed to delete Courier";
      });
  },
});

export const selectCourier = (state: RootState) => state.courier;

export default courierSlice.reducer;
